/**
 * A notebook that was an end-to-end encrypted folder in the previous BrainLog. Its pages arrive as
 * they were stored (ciphertext). Opening it once with the folder's passphrase converts it here, in
 * the browser: each page is decrypted with the old key and saved as a note under the notebook's new
 * key, which opens with the same passphrase. The server never sees the passphrase or the text.
 */
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import * as Y from "yjs";
import { toast } from "sonner";
import { Eye, EyeOff, LoaderCircle, LockKeyhole } from "lucide-react";
import { get, post, errorMessage } from "@/lib/api";
import type { Workspace } from "@/lib/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { buildScope, enableProjectEncryption } from "./convert";
import { rememberScopeKey, unlockScope, getScopeKey, type ScopeInfo } from "./keyring";
import { encSnapshot, encTitle, encVersion, requireKey } from "./ops";
import type { EncryptionStatus } from "./convert";

interface LegacyState {
  keySalt: string;
  keyCheck: string | null;
  pages: { id: string; title: string; folderId: string | null; position: number; envelope: string | null; deleted: boolean }[];
  converted: number;
  canConvert: boolean;
  ownerName: string | null;
  workspaceScopeId: string | null;
}

const ENVELOPE = "brainlog:enc:v1:";
const fromB64 = (s: string) => Uint8Array.from(atob(s), (c) => c.charCodeAt(0));

/** The previous BrainLog's key: PBKDF2-SHA256, 600,000 rounds, AES-GCM-256 (as in its folder_key.js). */
async function legacyKey(passphrase: string, saltHex: string) {
  const salt = new Uint8Array(saltHex.match(/.{2}/g)!.map((h) => parseInt(h, 16)));
  const material = await crypto.subtle.importKey("raw", new TextEncoder().encode(passphrase), "PBKDF2", false, ["deriveKey"]);
  return crypto.subtle.deriveKey({ name: "PBKDF2", salt, iterations: 600_000, hash: "SHA-256" }, material, { name: "AES-GCM", length: 256 }, false, ["decrypt"]);
}

async function legacyDecrypt(key: CryptoKey, envelope: string) {
  if (!envelope) return "";
  // A page saved before the folder was encrypted is plain text.
  if (!envelope.startsWith(ENVELOPE)) return envelope;
  const [iv, ct] = envelope.slice(ENVELOPE.length).split(":");
  return new TextDecoder().decode(await crypto.subtle.decrypt({ name: "AES-GCM", iv: fromB64(iv) }, key, fromB64(ct)));
}

async function passphraseIsRight(key: CryptoKey, state: LegacyState) {
  try {
    if (state.keyCheck) {
      const both = fromB64(state.keyCheck);
      const plain = await crypto.subtle.decrypt({ name: "AES-GCM", iv: both.slice(0, 12) }, key, both.slice(12));
      return new TextDecoder().decode(plain) === "brainlog-key-check-v1";
    }
    const sample = state.pages.find((p) => p.envelope?.startsWith(ENVELOPE));
    if (sample) await legacyDecrypt(key, sample.envelope!);
    return true;
  } catch {
    return false;
  }
}

export function LegacyUnlockPanel({ workspace }: { workspace: Workspace }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["legacy-encryption", workspace.id], queryFn: () => get<{ legacy: LegacyState | null }>(`/api/workspaces/${workspace.id}/legacy-encryption`) });
  const [passphrase, setPassphrase] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState("");
  const [error, setError] = useState<string | null>(null);
  const state = q.data?.legacy;

  const convert = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!state) return;
    setBusy(true);
    setError(null);
    try {
      setProgress("Checking the passphrase…");
      const oldKey = await legacyKey(passphrase, state.keySalt);
      if (!(await passphraseIsRight(oldKey, state))) throw new Error("This passphrase does not open this notebook. Use the one you chose for the folder in the previous BrainLog.");

      // The notebook's new key, opened with the same passphrase (set up once; resumed after an interruption).
      let scopeId = state.workspaceScopeId;
      if (!scopeId) {
        setProgress("Setting up the notebook's encryption…");
        const scope = await buildScope(passphrase, null);
        await enableProjectEncryption(workspace.id, scope);
        scopeId = scope.id;
      } else if (!getScopeKey(scopeId)) {
        const status = await get<EncryptionStatus>(`/api/workspaces/${workspace.id}/encryption`);
        const scope = status.scopes.find((s: ScopeInfo) => s.id === scopeId);
        if (!scope) throw new Error("The notebook's key could not be found. Reload and try again.");
        await unlockScope(scope, passphrase, "passphrase");
      }
      const key = requireKey(scopeId);

      for (const [i, p] of state.pages.entries()) {
        setProgress(`Converting page ${i + 1} of ${state.pages.length}…`);
        const text = await legacyDecrypt(oldKey, p.envelope ?? "");
        const id = crypto.randomUUID();
        const versionId = crypto.randomUUID();
        const doc = new Y.Doc();
        doc.getText("content").insert(0, text);
        const snapshot = Y.encodeStateAsUpdate(doc);
        doc.destroy();
        try {
          await post("/api/notes/encrypted", {
            id,
            workspaceId: workspace.id,
            folderId: p.folderId,
            scopeId,
            keyProof: key.proof,
            encTitle: await encTitle(scopeId, id, p.title),
            snapshot: await encSnapshot(scopeId, id, snapshot),
            version: { id: versionId, blob: await encVersion(scopeId, id, versionId, { title: p.title, text }) },
            legacyPageId: p.id,
          });
        } catch (err) {
          // Converted meanwhile (another tab, or an answer that got lost): nothing to do.
          if (!/already converted/i.test(errorMessage(err))) throw err;
        }
      }
      if (!state.pages.length) await post(`/api/workspaces/${workspace.id}/legacy-encryption/finish`, {});
      await rememberScopeKey(scopeId, key.raw);
      toast.success(`“${workspace.name}” is open. It now uses the new encryption, with the same passphrase.`);
      await qc.invalidateQueries();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
      setProgress("");
    }
  };

  return (
    <div className="flex h-full items-start justify-center overflow-y-auto p-6">
      <div className="w-full max-w-lg space-y-4 rounded-xl border bg-card p-6 shadow-sm">
        <LockKeyhole className="size-8 text-primary" aria-hidden />
        <h2 className="font-display text-xl font-semibold">Open “{workspace.name}”</h2>
        <p className="text-sm text-muted-foreground">
          This notebook was an encrypted folder in the previous BrainLog. Enter its passphrase once: your pages are unlocked here, in your browser, and saved with the new
          encryption. From then on it opens with the same passphrase. BrainLog never sees the passphrase or your text.
        </p>
        {q.isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
        {q.isError && <p className="text-sm text-destructive">{errorMessage(q.error)}</p>}
        {state && !state.canConvert && (
          <p className="rounded-md border bg-muted/40 p-3 text-sm" role="note">
            Only {state.ownerName ?? "the owner"} can do this, once. Then you can open it with the passphrase you already know.
          </p>
        )}
        {state && state.canConvert && (
          <form className="space-y-3" onSubmit={convert}>
            <p className="text-sm">
              {state.pages.length} page{state.pages.length === 1 ? "" : "s"} to open{state.converted ? ` (${state.converted} already done)` : ""}.
            </p>
            <div className="space-y-1.5">
              <Label htmlFor="legacy-passphrase">Passphrase of the folder</Label>
              <div className="relative">
                <Input id="legacy-passphrase" type={show ? "text" : "password"} value={passphrase} onChange={(e) => setPassphrase(e.target.value)} autoComplete="off" className="pr-10" required autoFocus />
                <button type="button" onClick={() => setShow((s) => !s)} className="absolute inset-y-0 right-0 flex w-10 items-center justify-center text-muted-foreground" aria-label={show ? "Hide passphrase" : "Show passphrase"}>
                  {show ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                </button>
              </div>
            </div>
            {error && (
              <p className="text-sm text-destructive" role="alert">
                {error}
              </p>
            )}
            <Button type="submit" disabled={busy || !passphrase}>
              {busy && <LoaderCircle className="animate-spin" />} {busy ? progress || "Working…" : "Open the notebook"}
            </Button>
            <p className="text-xs text-muted-foreground">Forgot it? Nobody can recover it, not even us: that is what kept these pages private.</p>
          </form>
        )}
      </div>
    </div>
  );
}
